import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  useAdvancedExerciseSearch,
  useExerciseFilterOptions,
  useMuscleOptions,
  useEquipmentOptions,
  useDemonstratorOptions,
} from '@/hooks/useAdvancedExerciseSearch';
import { useExerciseBookmarks } from '@/hooks/useExerciseBookmarks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusDot } from '@/components/ui/status-dot';
import {
  SlidersHorizontal, Search, Video, BarChart, FileClock, ShieldCheck,
  Zap, TrendingUp, Users, Calendar, FilterX, ChevronDown, ChevronRight,
  Dumbbell, Activity, Target, ArrowUpDown, Library, Eye,
  Bookmark, BookmarkCheck, UserPlus,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import BookmarkForAthleteDialog from '@/components/exercises/BookmarkForAthleteDialog';

function ResultsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-4 rounded-lg border">
          <Skeleton className="h-12 w-12 rounded" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-full max-w-md" />
          </div>
          <Skeleton className="h-5 w-16" />
        </div>
      ))}
    </div>
  );
}

function CollapsibleSection({ title, icon: Icon, children, defaultOpen = true, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full text-left py-2"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{title}</span>
        {badge > 0 && (
          <Badge variant="default" className="ml-auto text-[10px] px-1.5 py-0">{badge}</Badge>
        )}
      </button>
      {open && <div className="pb-4">{children}</div>}
    </div>
  );
}

function FilterSelect({ value, onValueChange, label, options, placeholder, disabled }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>
      <Select value={value || 'all'} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          {options?.map((option) => {
            const val = typeof option === 'object' ? option.value : option;
            const label = typeof option === 'object' ? option.label : option;
            return <SelectItem key={val} value={val}>{label}</SelectItem>;
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

const AdvancedSearch = () => {
  const { can } = useAuth();
  const canSeeEngagement = can('exercises.engagement');
  const {
    filters,
    exercises,
    totalCount,
    loading,
    error,
    updateFilters,
    resetFilters,
    applyPreset,
    activePreset,
    activeFilterCount,
    pagination,
  } = useAdvancedExerciseSearch();

  const { isBookmarked, toggleBookmark } = useExerciseBookmarks();
  const [bookmarkForAthleteExercise, setBookmarkForAthleteExercise] = useState(null);
  const { filterOptions, loading: optionsLoading } = useExerciseFilterOptions();
  const { muscleOptions, muscleGroups } = useMuscleOptions();
  const { equipmentOptions } = useEquipmentOptions();
  const { demonstrators } = useDemonstratorOptions();

  const handleFilterChange = (key, value) => {
    updateFilters({ [key]: value === '' ? null : value });
  };

  const presetButtons = [
    { id: 'needsVideo', label: 'Needs Video', icon: Video },
    { id: 'unused', label: 'Unused', icon: FileClock },
    { id: 'popular', label: 'Popular', icon: TrendingUp },
    { id: 'beginnerFriendly', label: 'Beginner', icon: Users },
    { id: 'returnToSport', label: 'Return to Sport', icon: ShieldCheck },
    { id: 'needsSync', label: 'Needs Sync', icon: Zap },
  ];

  const basicActiveCount = [filters.difficultyFilter, filters.regionFilter, filters.rehabStageFilter]
    .filter(v => v !== 'all').length + (filters.searchTerm ? 1 : 0);
  const movementActiveCount = [filters.phase, filters.pathTypeFilter, filters.movementTypeFilter, filters.bilateralFilter, filters.executionFilter]
    .filter(v => v !== 'all').length;
  const contentActiveCount = [filters.equipmentFilter, filters.muscleFilter, filters.videoStatus, filters.usageFilter, filters.demonstratorFilter]
    .filter(v => v !== 'all').length + (filters.showCoachOnly !== null ? 1 : 0);

  const muscleFilterOptions = [
    ...muscleGroups.map(g => ({ value: g, label: `[Group] ${g}` })),
    ...muscleOptions.map(m => ({ value: m.name, label: m.name })),
  ];

  const equipmentFilterOptions = equipmentOptions.map(e => ({ value: e.name, label: e.name }));

  const demonstratorFilterOptions = [
    { value: 'none', label: 'No Demonstrator' },
    ...demonstrators.map(d => ({ value: d.name, label: d.name })),
  ];

  return (
    <>
      <Helmet><title>Advanced Search - My Movement Space</title></Helmet>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Advanced Exercise Search</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Filter, audit, and explore the exercise library.
            </p>
          </div>
          {activeFilterCount > 0 && (
            <Button variant="outline" size="sm" onClick={resetFilters}>
              <FilterX className="h-4 w-4 mr-2" />
              Clear {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
            </Button>
          )}
        </div>

        {/* Search + Sort */}
        <Card className="shadow-none">
          <CardContent className="pt-4 pb-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, description, instructions..."
                    className="pl-10 h-9"
                    value={filters.searchTerm || ''}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value || null)}
                  />
                </div>
              </div>
              <Select value={filters.sortBy} onValueChange={(v) => handleFilterChange('sortBy', v)}>
                <SelectTrigger className="h-9 text-xs">
                  <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name_asc">Name (A-Z)</SelectItem>
                  <SelectItem value="name_desc">Name (Z-A)</SelectItem>
                  <SelectItem value="usage_desc">Most Used</SelectItem>
                  {canSeeEngagement && <SelectItem value="popularity_desc">Most Popular</SelectItem>}
                  <SelectItem value="recent_desc">Most Recent</SelectItem>
                  <SelectItem value="difficulty_asc">Easiest First</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Quick Presets */}
            <div className="flex flex-wrap gap-2">
              {presetButtons.map(preset => (
                <Button
                  key={preset.id}
                  variant={activePreset === preset.id ? 'default' : 'outline'}
                  size="xs"
                  onClick={() => applyPreset(preset.id)}
                >
                  <preset.icon className="h-3.5 w-3.5 mr-1.5" />
                  {preset.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Filter Panels */}
        <Card className="shadow-none">
          <CardContent className="pt-4 pb-2 divide-y divide-border">
            <CollapsibleSection title="Basic Filters" icon={SlidersHorizontal} defaultOpen={true} badge={basicActiveCount}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <FilterSelect
                  value={filters.difficultyFilter}
                  onValueChange={(v) => handleFilterChange('difficultyFilter', v)}
                  label="Difficulty"
                  options={filterOptions.difficulty}
                  disabled={optionsLoading}
                />
                <FilterSelect
                  value={filters.regionFilter}
                  onValueChange={(v) => handleFilterChange('regionFilter', v)}
                  label="Primary Region"
                  options={filterOptions.primary_region}
                  disabled={optionsLoading}
                />
                <FilterSelect
                  value={filters.rehabStageFilter}
                  onValueChange={(v) => handleFilterChange('rehabStageFilter', v)}
                  label="Rehab Stage"
                  options={filterOptions.stage_of_rehab}
                  disabled={optionsLoading}
                />
                <FilterSelect
                  value={filters.searchMode}
                  onValueChange={(v) => handleFilterChange('searchMode', v)}
                  label="Search Mode"
                  options={[
                    { value: 'exact', label: 'Exact Match' },
                    { value: 'fuzzy', label: 'Fuzzy' },
                    { value: 'fulltext', label: 'Full Text' },
                  ]}
                />
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Movement & Phase" icon={Activity} defaultOpen={false} badge={movementActiveCount}>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <FilterSelect
                  value={filters.phase}
                  onValueChange={(v) => handleFilterChange('phase', v)}
                  label="Exercise Phase"
                  options={filterOptions.exercise_phase}
                  disabled={optionsLoading}
                />
                <FilterSelect
                  value={filters.pathTypeFilter}
                  onValueChange={(v) => handleFilterChange('pathTypeFilter', v)}
                  label="Path Type"
                  options={filterOptions.exercise_path}
                  disabled={optionsLoading}
                />
                <FilterSelect
                  value={filters.movementTypeFilter}
                  onValueChange={(v) => handleFilterChange('movementTypeFilter', v)}
                  label="Movement Type"
                  options={[
                    { value: 'static', label: 'Static' },
                    { value: 'dynamic', label: 'Dynamic' },
                    { value: 'flow', label: 'Flow' },
                    { value: 'ballistic', label: 'Ballistic' },
                  ]}
                />
                <FilterSelect
                  value={filters.bilateralFilter}
                  onValueChange={(v) => handleFilterChange('bilateralFilter', v)}
                  label="Laterality"
                  options={[
                    { value: 'bilateral', label: 'Bilateral' },
                    { value: 'unilateral', label: 'Unilateral' },
                    { value: 'alternating', label: 'Alternating' },
                  ]}
                />
                <FilterSelect
                  value={filters.executionFilter}
                  onValueChange={(v) => handleFilterChange('executionFilter', v)}
                  label="Execution"
                  options={[
                    { value: 'simultaneous', label: 'Simultaneous' },
                    { value: 'alternating', label: 'Alternating' },
                    { value: 'independent', label: 'Independent' },
                    { value: 'reciprocal', label: 'Reciprocal' },
                  ]}
                />
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Equipment, Muscles & Content" icon={Dumbbell} defaultOpen={false} badge={contentActiveCount}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <FilterSelect
                  value={filters.equipmentFilter}
                  onValueChange={(v) => handleFilterChange('equipmentFilter', v)}
                  label="Equipment"
                  options={equipmentFilterOptions}
                />
                <FilterSelect
                  value={filters.muscleFilter}
                  onValueChange={(v) => handleFilterChange('muscleFilter', v)}
                  label="Muscle / Group"
                  options={muscleFilterOptions}
                />
                <FilterSelect
                  value={filters.demonstratorFilter}
                  onValueChange={(v) => handleFilterChange('demonstratorFilter', v)}
                  label="Demonstrator"
                  options={demonstratorFilterOptions}
                />
                <FilterSelect
                  value={filters.videoStatus}
                  onValueChange={(v) => handleFilterChange('videoStatus', v)}
                  label="Video Status"
                  options={[
                    { value: 'has_video', label: 'Has Video' },
                    { value: 'no_video', label: 'No Video' },
                    { value: 'needs_sync', label: 'Needs Sync' },
                  ]}
                />
                <FilterSelect
                  value={filters.usageFilter}
                  onValueChange={(v) => handleFilterChange('usageFilter', v)}
                  label="Usage"
                  options={[
                    { value: 'used', label: 'Used' },
                    { value: 'unused', label: 'Unused' },
                    { value: 'popular', label: 'Popular (5+)' },
                  ]}
                />
                <FilterSelect
                  value={filters.showCoachOnly === null ? 'all' : filters.showCoachOnly ? 'true' : 'false'}
                  onValueChange={(v) => handleFilterChange('showCoachOnly', v === 'all' ? null : v === 'true')}
                  label="Library"
                  options={[
                    { value: 'true', label: 'Coach Library Only' },
                    { value: 'false', label: 'Custom Only' },
                  ]}
                />
              </div>
            </CollapsibleSection>
          </CardContent>
        </Card>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium">
              {loading ? 'Searching...' : `${totalCount} exercise${totalCount !== 1 ? 's' : ''} found`}
            </h2>
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="text-xs">{activeFilterCount} active filter{activeFilterCount !== 1 ? 's' : ''}</Badge>
            )}
          </div>

          {error && (
            <Card className="shadow-none mb-4">
              <CardContent className="py-6"><p className="text-sm text-destructive">{error}</p></CardContent>
            </Card>
          )}

          {loading ? (
            <ResultsSkeleton />
          ) : exercises.length === 0 ? (
            <div className="text-center py-16 border rounded-lg">
              <Search className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium">No exercises match your criteria</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">Try adjusting your filters or search term.</p>
              <Button variant="outline" size="sm" onClick={resetFilters}>Reset Filters</Button>
            </div>
          ) : (
            <div className="border rounded-lg divide-y">
              {exercises.map((exercise) => {
                const bookmarked = isBookmarked(exercise.id);
                return (
                <div
                  key={exercise.id}
                  className="flex items-start gap-4 p-4 hover:bg-muted/40 transition-colors"
                >
                  {/* Bookmark */}
                  <div className="flex flex-col items-center gap-0.5 shrink-0 mt-0.5">
                    <button
                      onClick={() => toggleBookmark(exercise.id)}
                      className={`p-1 rounded hover:bg-muted transition-colors ${bookmarked ? 'text-amber-500' : 'text-muted-foreground/40 hover:text-muted-foreground'}`}
                    >
                      {bookmarked ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => setBookmarkForAthleteExercise(exercise)}
                      className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground/40 hover:text-muted-foreground"
                      title="Bookmark for athlete"
                    >
                      <UserPlus className="h-4 w-4" />
                    </button>
                  </div>

                  <Link
                    to={`/exercise/${exercise.id}`}
                    className="flex items-start gap-4 flex-1 min-w-0"
                  >
                  {/* Thumbnail */}
                  <div className="h-12 w-12 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                    {exercise.video_thumbnail_url ? (
                      <img src={exercise.video_thumbnail_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <Dumbbell className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{exercise.name}</span>
                      {exercise.is_admin_library && (
                        <Badge variant="default" className="text-[10px] px-1.5 py-0">Coach</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{exercise.description}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                      {exercise.difficulty && (
                        <span className="flex items-center gap-1"><BarChart className="h-3 w-3" />{exercise.difficulty}</span>
                      )}
                      {exercise.primary_region && (
                        <span className="flex items-center gap-1"><Target className="h-3 w-3" />{exercise.primary_region}</span>
                      )}
                      <span className="flex items-center gap-1"><FileClock className="h-3 w-3" />{exercise.usage_count} uses</span>
                      {canSeeEngagement && (
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{exercise.total_user_completions} completions</span>
                      )}
                      {canSeeEngagement && exercise.avg_rpe != null && (
                        <span className="flex items-center gap-1"><Activity className="h-3 w-3" />RPE {exercise.avg_rpe}</span>
                      )}
                      {exercise.demonstrator_name && (
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{exercise.demonstrator_name}</span>
                      )}
                      {exercise.target_muscles?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Dumbbell className="h-3 w-3" />
                          {exercise.target_muscles.slice(0, 3).join(', ')}
                          {exercise.target_muscles.length > 3 && ` +${exercise.target_muscles.length - 3}`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right side status */}
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {exercise.has_video ? (
                      <Badge variant="success" className="text-[10px]">
                        <Eye className="h-3 w-3 mr-1" />Video
                      </Badge>
                    ) : (
                      <Badge variant="warning" className="text-[10px]">No Video</Badge>
                    )}
                    {exercise.program_count > 0 && (
                      <span className="text-[10px] text-muted-foreground">{exercise.program_count} program{exercise.program_count !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                  </Link>
                </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {exercises.length > 0 && (
            <div className="flex justify-center items-center mt-6 gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={pagination.previousPage}
                disabled={!pagination.hasPreviousPage || loading}
              >
                Previous
              </Button>
              <span className="text-xs text-muted-foreground tabular-nums">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={pagination.nextPage}
                disabled={!pagination.hasNextPage || loading}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      <BookmarkForAthleteDialog
        exerciseId={bookmarkForAthleteExercise?.id}
        exerciseName={bookmarkForAthleteExercise?.name}
        open={!!bookmarkForAthleteExercise}
        onOpenChange={(open) => { if (!open) setBookmarkForAthleteExercise(null); }}
      />
    </>
  );
};

export default AdvancedSearch;
